/**
 * Referral surface (WP-F10). ux-flows §1.H puts referral / free-unlock credits in Settings, and
 * design-spec has no dedicated referral screen (a known design gap), so this is a straight adaptation
 * of The Thread system: reached from a Settings row, it is one focused screen with two halves —
 *
 *  - **Share your code** (the give side): the candidate's own code + `{base}/{code}` link, copy-link
 *    and copy-code affordances, and an "email an invite" mailto (the web delta — no native share
 *    sheet; DESIGN-NOTES delta a). A sage strip surfaces the earned bonus when `bonusEarned` is true.
 *  - **Have an invite code?** (the claim side): a single input → `POST /api/referrals/claim`. Each
 *    ProblemDetails outcome (invalid_code / self_referral / already_referred / not_eligible) maps to
 *    honest copy. The claimant earns nothing at MVP — the grant is the *referrer's* — so the copy
 *    frames claiming as crediting the friend who invited them, never a bonus to the claimant.
 *
 * Real wiring: `getReferral` (GET /api/referrals/me, minted lazily) + `claimReferral`
 * (POST /api/referrals/claim), both live (WP-B18).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, CandidateApiError } from '@/lib/api-client';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { ThreadMark } from '@/components/ThreadMark';

/** Group an 8-char code 4-4 for legibility (JANE7Q2K → JANE-7Q2K). The API's normalize strips the
 *  hyphen again on claim, and the copy actions copy the canonical (unhyphenated) form regardless. */
function formatCode(code: string): string {
  return code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
}

function BackLink() {
  return (
    <Link
      to="/settings"
      className="-ml-1 inline-flex items-center gap-1 rounded-md px-1 py-1 font-body text-[13px] font-medium text-ink-quiet transition-colors duration-ui ease-thread hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25"
    >
      ‹ Settings
    </Link>
  );
}

/** A copy-to-clipboard button that flips to "Copied" for ~2s, with an aria-live confirmation. */
function CopyButton({ label, value, className }: { label: string; value: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  async function onCopy() {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }
  return (
    <Button variant="ghost" size="sm" onClick={onCopy} className={className}>
      <span aria-live="polite">{copied ? 'Copied' : label}</span>
    </Button>
  );
}

/** Map a claim ProblemDetails code to honest, on-voice copy (04-copy: plain, warm, no blame). */
function claimErrorMessage(err: CandidateApiError): string {
  switch (err.code) {
    case 'invalid_code':
      return 'That code doesn’t look right. Check it and try again.';
    case 'self_referral':
      return 'That’s your own code — you can’t refer yourself.';
    case 'already_referred':
      return 'You’ve already added an invite code.';
    case 'not_eligible':
      return 'Invite codes can only be added before your first reference request.';
    default:
      return 'We couldn’t add that code just now. Try again in a moment.';
  }
}

/** The "give" half: the candidate's code + link + copy / email-invite affordances. */
function ShareSection({ code, shareUrl, bonusEarned }: { code: string; shareUrl: string; bonusEarned: boolean }) {
  const emailInvite = `mailto:?subject=${encodeURIComponent('An invite to By My Word')}&body=${encodeURIComponent(
    `I’ve been using By My Word to gather references — no awkward asking, and the referee approves every word themselves. ` +
      `Here’s my invite link: ${shareUrl} — By My Word`,
  )}`;

  return (
    <section className="mt-6">
      <h2 className="font-display text-[13px] font-semibold uppercase tracking-[0.06em] text-ink-quiet">
        Share your invite
      </h2>
      <div className="mt-2 rounded-lg border border-line bg-surface p-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-quiet">Your invite code</p>
          <div className="mt-1.5 flex items-center justify-between gap-3">
            <span className="font-display text-[22px] font-semibold tracking-[0.14em] text-ink" aria-label={`Invite code ${code}`}>
              {formatCode(code)}
            </span>
            <CopyButton label="Copy code" value={code} className="flex-none" />
          </div>
        </div>

        <div className="mt-4 border-t border-line pt-4">
          <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-quiet">Your invite link</p>
          <div className="mt-1.5 flex items-center justify-between gap-3">
            <span className="min-w-0 truncate font-body text-[13.5px] text-ink" title={shareUrl}>
              {shareUrl.replace(/^https?:\/\//, '')}
            </span>
            <CopyButton label="Copy link" value={shareUrl} className="flex-none" />
          </div>
          <Button asChild variant="ghost" size="sm" className="mt-3">
            <a href={emailInvite}>Email an invite</a>
          </Button>
        </div>
      </div>

      {bonusEarned && (
        <p className="mt-3 flex items-center gap-2.5 rounded-md bg-sage-tint px-3.5 py-3 font-body text-[13px] font-medium text-sage-text">
          <svg viewBox="0 0 24 24" fill="none" width={18} height={18} aria-hidden="true" className="flex-none">
            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Free unlock earned — thanks for spreading the word.
        </p>
      )}
    </section>
  );
}

/** The "claim" half: enter a friend's code → POST /api/referrals/claim, with per-outcome copy. */
function ClaimSection() {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const claim = useMutation({
    mutationFn: (value: string) => apiClient.claimReferral(value),
    onSuccess: () => {
      setError(null);
      setDone(true);
      // A successful claim can flip the caller's own referral surface (and, later, allowance).
      void queryClient.invalidateQueries({ queryKey: ['referral'] });
    },
    onError: (caught) => {
      setDone(false);
      setError(caught instanceof CandidateApiError ? claimErrorMessage(caught) : 'We couldn’t add that code just now. Try again in a moment.');
    },
  });

  const trimmed = code.trim();

  if (done) {
    return (
      <section className="mt-6">
        <h2 className="font-display text-[13px] font-semibold uppercase tracking-[0.06em] text-ink-quiet">
          Have an invite code?
        </h2>
        <div className="mt-2 rounded-lg border border-line bg-surface-tint p-4">
          <p role="status" className="font-body text-[13.5px] leading-relaxed text-ink">
            Added — thank you. The friend who invited you gets their free unlock once you send your first reference request.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-6">
      <h2 className="font-display text-[13px] font-semibold uppercase tracking-[0.06em] text-ink-quiet">
        Have an invite code?
      </h2>
      <div className="mt-2 rounded-lg border border-line bg-surface p-4">
        <p className="font-body text-[13px] leading-relaxed text-ink-quiet">
          Add a friend’s code to credit them. It can only be added before your first reference request.
        </p>
        <form
          className="mt-3 flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmed) claim.mutate(trimmed);
          }}
        >
          <div className="flex-1">
            <label htmlFor="referral-code" className="mb-1.5 block text-[12px] font-medium text-ink-quiet">
              Invite code
            </label>
            <input
              id="referral-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError(null);
              }}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="ABCD-EFGH"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'referral-code-error' : undefined}
              className="min-h-[44px] w-full rounded-md border border-line-2 bg-surface px-3.5 py-2 font-body text-[14px] font-medium uppercase tracking-[0.08em] text-ink outline-none transition-colors duration-ui ease-thread placeholder:font-normal placeholder:tracking-normal placeholder:text-ink-quiet focus:border-sage focus:ring-2 focus:ring-sage-deep/20"
            />
          </div>
          <Button type="submit" size="sm" disabled={!trimmed || claim.isPending} className="mb-px flex-none">
            {claim.isPending ? 'Adding…' : 'Add code'}
          </Button>
        </form>
        {error && (
          <p id="referral-code-error" role="alert" className="mt-2 text-[11.5px] font-medium leading-snug text-coral-text">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

export function Referral() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['referral'],
    queryFn: () => apiClient.getReferral(),
  });

  return (
    <AppShell title="Invite">
      <BackLink />
      <h1 className="mt-2 font-display text-[22px] font-semibold tracking-[-0.02em] text-ink">Invite a friend</h1>
      <p className="mt-1.5 font-body text-[14px] leading-relaxed text-ink-quiet">
        A good word travels. When someone you invite signs up and sends their first reference
        request, you’ll earn a free unlock — our thanks.
      </p>

      {isPending ? (
        <div className="mt-10 flex flex-col items-center gap-3" aria-busy="true">
          <ThreadMark className="h-9 w-auto" />
          <p className="font-body text-[13px] text-ink-quiet">Loading your invite…</p>
        </div>
      ) : isError ? (
        <div role="alert" className="mt-8 rounded-lg border border-line bg-surface p-5 text-center">
          <p className="font-body text-[14px] text-ink">We couldn’t load your invite just now.</p>
          <Button variant="ghost" size="sm" className="mt-3" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <>
          <ShareSection code={data.code} shareUrl={data.shareUrl} bonusEarned={data.bonusEarned} />
          <ClaimSection />
        </>
      )}

      <p className="mt-8 pb-4 text-center font-body text-[11px] text-ink-quiet">
        From their lips to your ledger.
      </p>
    </AppShell>
  );
}
